import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '@/components/base/Input';
import Button from '@/components/base/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/feature/ToastProvider';
import Loading from '@/components/base/Loading';

const LOGO_URL =
  'https://static.readdy.ai/image/5e0dc67cda370742b3d5bcd513fed04e/7ccb60a178e69ccb6f070627bdcb7304.jpeg';

export default function LoginPage() {
  const { session, loading, signIn } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ username?: string; password?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && session) {
      navigate('/', { replace: true });
    }
  }, [loading, session, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: { username?: string; password?: string } = {};

    if (!username.trim()) nextErrors.username = 'El usuario es obligatorio.';
    if (!password) nextErrors.password = 'La contraseña es obligatoria.';

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    setErrors({});
    const { error } = await signIn(username, password);
    setSubmitting(false);

    if (error) {
      setErrors({ form: error });
      notify({ tone: 'error', title: 'No se pudo iniciar sesión', message: error });
      return;
    }

    notify({ tone: 'success', title: 'Bienvenido', message: 'Sesión iniciada correctamente.' });
    navigate('/', { replace: true });
  };

  if (loading) {
    return <Loading fullScreen label="Cargando sistema..." />;
  }

  return (
    <div className="flex min-h-screen bg-background-50">
      <div
        className="relative hidden w-1/2 flex-col justify-between overflow-hidden p-10 lg:flex"
        style={{
          backgroundImage:
            'linear-gradient(140deg, oklch(var(--accent-700)), oklch(var(--secondary-700)) 55%, oklch(var(--primary-700)))',
        }}
      >
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-12 w-12 overflow-hidden rounded-md border border-white/30 bg-white/90">
            <img src={LOGO_URL} alt="IZZY Corporation" className="h-full w-full object-cover" />
          </div>
          <div>
            <p className="font-heading text-base font-bold text-white">IZZY Corporation</p>
            <p className="text-xs text-white/80">Sistema de Seguimiento</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h2 className="font-heading text-3xl font-extrabold leading-tight text-white">
            Controle sus proyectos, cobros y contratos en un solo lugar.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-white/85">
            Administre el seguimiento de los proyectos desarrollados para sus clientes, con
            recordatorios automáticos de cobros próximos y de contratos por finalizar.
          </p>

          <ul className="mt-8 flex flex-col gap-3">
            {[
              { icon: 'ri-dashboard-3-line', text: 'Panel general de proyectos' },
              { icon: 'ri-calendar-check-line', text: 'Alertas de cobro y contrato' },
              { icon: 'ri-shield-user-line', text: 'Acceso seguro por roles' },
            ].map((item) => (
              <li key={item.text} className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white/15 text-white">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <span className="text-sm text-white/90">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative z-10 text-xs text-white/70">
          © {new Date().getFullYear()} IZZY Corporation. Todos los derechos reservados.
        </p>

        <div className="absolute -right-24 top-1/4 h-72 w-72 rounded-full bg-white/10 blur-2xl"></div>
        <div className="absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-white/10 blur-3xl"></div>
      </div>

      <div className="flex w-full items-center justify-center px-4 py-10 lg:w-1/2">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 flex flex-col items-center gap-3 text-center lg:hidden">
            <div className="h-16 w-16 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <img src={LOGO_URL} alt="IZZY Corporation" className="h-full w-full object-cover" />
            </div>
            <div>
              <p className="font-heading text-base font-bold text-foreground-950">
                IZZY Corporation
              </p>
              <p className="text-xs text-foreground-500">Sistema de Seguimiento</p>
            </div>
          </div>

          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">
            Sistema de Seguimiento
          </h1>
          <p className="mt-1 text-sm text-foreground-600">
            Ingrese sus credenciales para continuar.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4" noValidate>
            <Input
              label="Usuario"
              name="username"
              icon="ri-user-line"
              placeholder="Ingrese su usuario"
              value={username}
              autoComplete="username"
              onChange={(event) => {
                setUsername(event.target.value);
                if (errors.username || errors.form) {
                  setErrors((prev) => ({ ...prev, username: undefined, form: undefined }));
                }
              }}
              error={errors.username}
            />

            <Input
              label="Contraseña"
              name="password"
              icon="ri-lock-line"
              type={showPassword ? 'text' : 'password'}
              placeholder="Ingrese su contraseña"
              value={password}
              autoComplete="current-password"
              onChange={(event) => {
                setPassword(event.target.value);
                if (errors.password || errors.form) {
                  setErrors((prev) => ({ ...prev, password: undefined, form: undefined }));
                }
              }}
              error={errors.password}
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
                </button>
              }
            />

            {errors.form && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
                  <i className="ri-error-warning-line text-base"></i>
                </span>
                <span>{errors.form}</span>
              </div>
            )}

            <Button type="submit" size="lg" fullWidth icon="ri-login-circle-line" loading={submitting}>
              Iniciar sesión
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-foreground-500">
            Acceso restringido. Solo personal autorizado de IZZY Corporation.
          </p>
        </div>
      </div>
    </div>
  );
}